import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { SubscriptionStatus } from '../../../models/subscription-usage.model';
import { forkJoin } from 'rxjs';
import { CharacterDeleteConfirmationComponent } from '../character-delete-confirmation/character-delete-confirmation.component';
import { InnerCommunicationDataPassService } from '../../services/inner-communication-data-pass.service';
import { DowngradeStatusResponse } from '../../models/downgrade-response.model';

@Component({
    selector: 'app-game-delete-confirmation',
    templateUrl: './game-delete-confirmation.component.html',
    styleUrls: ['./game-delete-confirmation.component.scss'],
    standalone: false
})
export class GameDeleteConfirmationComponent implements OnInit {

  upgradePlan: any;
  currentPlan: any;
  planUsage: SubscriptionStatus;
  userId: any;
  gameList: any[] = [];
  isLoading = false;
  deletedMapIds: any[] = [];
  autoDeletedGames: any[] = [];
  manualGamesToDelete: number = 0;
  downgradeResponse!: DowngradeStatusResponse;

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private router: Router,
    private dataService: DataService,
    private currentUserService: CurrentUserService,
    private innerCommunicationDataPassService: InnerCommunicationDataPassService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.setupCurrentUserPlanUsage();
    this.getGameList();
  }

  setupCurrentUserPlanUsage() {
    this.currentUserService.planUsageData.subscribe((data: SubscriptionStatus) => {
      if (data) {
        this.planUsage = data;
        this.cdr.detectChanges();
      }
    });
  }

  manuallyDeletedGameIds: any[] = [];

  getGameList() {
    this.dataService.getactivegames(this.userId).subscribe((res: { gamemasterdetails: any[] }) => {
      if (res.gamemasterdetails && res.gamemasterdetails?.length > 0) {
        this.gameList = res.gamemasterdetails.filter((g: any) => !this.manuallyDeletedGameIds.includes(g.id));
        this.calculateGamesToDelete();
      } else {
        this.gameList = [];
        this.calculateGamesToDelete();
      }
      this.cdr.detectChanges();
    });
  }

  calculateGamesToDelete() {
    this.autoDeletedGames = this.gameList.filter(g => this.deletedMapIds.includes(g.map_id));
    const totalGames = this.gameList.length;
    const maxGames = this.downgradeResponse?.limits_comparison?.games?.new_plan_limit ?? (this.upgradePlan?.features?.max_games_created || 0);
    
    if (maxGames === -1) {
      this.manualGamesToDelete = 0;
    } else {
      const remainingAfterAuto = totalGames - this.autoDeletedGames.length;
      this.manualGamesToDelete = Math.max(0, remainingAfterAuto - maxGames);
    }
  }

  isGameAutoDeleted(game: any): boolean {
    return this.deletedMapIds.includes(game.map_id);
  }

  close() {
    this.activeModal.close('close');
    this.innerCommunicationDataPassService.childModalCloseEvent(true);
  }

  deleteSingleGame(gameId: any) {
    if (confirm('Are you sure you want to mark this game for deletion?')) {
      this.manuallyDeletedGameIds.push(gameId);
      this.gameList = this.gameList.filter((g: any) => g.id !== gameId);
      this.calculateGamesToDelete();
    }
  }

  deleteAll() {
    this.isLoading = true;
    const deleteObservables = this.gameList.map(game => this.dataService.deleteGame(game.id));
    if (deleteObservables.length > 0) {
      forkJoin(deleteObservables).subscribe({
        next: () => {
          this.isLoading = false;
          this.getGameList(); // Refresh list (should be empty)
        
          this.cdr.detectChanges();},
        error: (err: any) => {
          console.error('Error deleting games', err);
          this.isLoading = false;
        
          this.cdr.detectChanges();}
      });
    }
  }

  deleteGames() {
    this.activeModal.close('game-delete');
    this.router.navigate(['activegames']);
  }

  next() {
    this.activeModal.close('next');
    const modalRef = this.modalService.open(CharacterDeleteConfirmationComponent, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      keyboard: false
    });
    modalRef.componentInstance.upgradePlan = this.upgradePlan;
    modalRef.componentInstance.currentPlan = this.currentPlan;
    modalRef.componentInstance.userId = this.userId;
    modalRef.componentInstance.deletedMapIds = this.deletedMapIds;
    modalRef.componentInstance.manuallyDeletedGameIds = this.manuallyDeletedGameIds;
    modalRef.componentInstance.downgradeResponse = this.downgradeResponse;
    
    modalRef.result.then((result) => {
      if (result === 'next') {
        this.activeModal.close('next');
      } else if (result === 'close') {
        this.activeModal.close('close');
      }
    
          this.cdr.detectChanges();});
  }

  subscribe() {
    this.activeModal.close('subscribe');
    this.router.navigate(['/subscription']);
  }
}
